import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { BdShareableSelectService } from '../../select/global-search/shareable-select.service';
import {
  ShareableObj,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';

import { BdRecentTabComponent } from '../recent-tab/recent-tab.component';
import { ItemNavigateEvent } from '../shareables-overview.models';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

/**
 * Standalone "recently used" shareables list — the flat recent table without
 * the overview's sidebar/tabs. Hosts {@link BdRecentTabComponent}, provides the
 * dialog service it needs, and routes row selection to the shareable's detail
 * view. Used wherever a feature only needs a quick list of recent items
 * (optionally scoped to a single {@link ShareableType}).
 */
@Component({
  selector: 'bd-recent-shareables',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!--
      The padding wrapper must stay part of the host's flex column: the recent
      tab and its table are sized with scrollHeight="flex", i.e. they expect an
      ancestor chain with a resolvable height so the table body scrolls
      internally and the pager stays pinned at the bottom. A plain block
      wrapper breaks that chain — the table then grows to its content and the
      surrounding page scrolls instead.
    -->
    <div class="p-4 flex flex-col flex-auto min-h-0">
      <bd-recent-tab
        [shareableType]="shareableType()"
        [showType]="showType()"
        (itemNavigate)="onItemNavigate($event)"
      ></bd-recent-tab>
    </div>
  `,
  host: {
    class: 'flex flex-col min-h-0',
  },
  providers: [BD_DIALOG_SERVICE_PROVIDER],
  imports: [BdRecentTabComponent],
})
export class BdRecentShareablesComponent {
  public readonly shareableType = input<ShareableType | undefined>(undefined);
  public readonly showType = input(false);

  private readonly _recentTab = viewChild(BdRecentTabComponent);
  private readonly _selectService = inject(BdShareableSelectService);

  public onItemNavigate(event: ItemNavigateEvent): void {
    this._selectService.navigate({
      id: event.id,
      type: event.type,
      name: '',
    } as ShareableObj);
  }

  /** Re-fetches the list — e.g. after a new shareable was created. */
  public reload(): void {
    this._recentTab()?.reload();
  }
}
